/**
 * The sign-in waiting for its code, kept per tab in sessionStorage.
 *
 * Phones discard a background tab while the person reads the code in their
 * mail app; the tab then reloads and the code screen is gone. Starting over
 * sends a new code, which kills the one they just read (2026-10-24: three
 * codes, one sign-in, Galaxy A05). With this record the code screen comes
 * back after the reload instead.
 */

const KEY = 'confy:pending-sign-in'

/** GoTrue's email OTP lives an hour. */
const MAX_AGE_MS = 60 * 60 * 1000

export interface PendingSignIn {
  email: string
  firstName: string
  lastName: string
  isNewUser: boolean
  googleMail: boolean
  returnTo?: string
  /** pathname + search the code was requested on; resumed only there. */
  path: string
  /** When the last code was sent (ms epoch). */
  sentAt: number
}

export function savePendingSignIn(pending: PendingSignIn): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(pending))
  } catch {
    // Private mode / storage blocked: the code screen just won't survive a reload.
  }
}

export function clearPendingSignIn(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // ignore
  }
}

/** The pending sign-in for this page, if one is still usable. */
export function loadPendingSignIn(path: string, now: number = Date.now()): PendingSignIn | null {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const p = JSON.parse(raw) as Partial<PendingSignIn>
    if (typeof p.email !== 'string' || !p.email || typeof p.path !== 'string' || typeof p.sentAt !== 'number') {
      clearPendingSignIn()
      return null
    }
    if (now - p.sentAt > MAX_AGE_MS) {
      clearPendingSignIn()
      return null
    }
    if (p.path !== path) return null
    return {
      email: p.email,
      firstName: typeof p.firstName === 'string' ? p.firstName : '',
      lastName: typeof p.lastName === 'string' ? p.lastName : '',
      isNewUser: p.isNewUser === true,
      googleMail: p.googleMail === true,
      returnTo: typeof p.returnTo === 'string' ? p.returnTo : undefined,
      path: p.path,
      sentAt: p.sentAt,
    }
  } catch {
    clearPendingSignIn()
    return null
  }
}
